import moment, { unitOfTime } from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  ALLOWED_ROLES_ENUM,
  MEMBER_STATUS_ENUM,
  REGEX,
  SIDEBAR_ROUTE_CONFIG,
} from "../constants/appConstants";
import { ERRORS } from "../constants/errors";
import { OPERATION_USER_SIDEBAR_OPTIONS } from "../constants/routes";
import {
  DropdownType,
  ExportMember,
  ObjectType,
  Route,
} from "../constants/types";

const configureSidebarOptions = (
  serverRoutes: Route[],
  selectedConfig: (typeof SIDEBAR_ROUTE_CONFIG)[keyof typeof SIDEBAR_ROUTE_CONFIG] = SIDEBAR_ROUTE_CONFIG.CLIENT_ONLY
) => {
  let configuredRoutes: Route[] = [];
  switch (selectedConfig) {
    case SIDEBAR_ROUTE_CONFIG.SERVER_ONLY:
      configuredRoutes = [...configuredRoutes, ...serverRoutes];
      break;
    case SIDEBAR_ROUTE_CONFIG.SERVER_CLIENT:
      configuredRoutes = [
        ...configuredRoutes,
        ...serverRoutes,
        ...OPERATION_USER_SIDEBAR_OPTIONS,
      ];
      break;
    case SIDEBAR_ROUTE_CONFIG.CLIENT_ONLY:
      configuredRoutes = [
        ...configuredRoutes,
        ...OPERATION_USER_SIDEBAR_OPTIONS,
      ];
      break;
    default:
      break;
  }
  const uniqueSortedRoutes: Route[] = [
    ...new Map(
      configuredRoutes
        .sort((a, b) => (a?.sequenceNumber ?? 0) - (b?.sequenceNumber ?? 0))
        .map((item) => [item.url, item])
    ).values(),
  ];
  return uniqueSortedRoutes;
};

const errorHandler =
  (callback: any) =>
  async (...args: any) => {
    try {
      return await callback(...args);
    } catch (error: any) {
      throw new Error(error.message || error || ERRORS.BAD_REQUEST);
    }
  };

const isRoleAllowed = (role: string): boolean => {
  const formattedValue = role?.toLowerCase?.()?.replace?.(/\s+/g, "");
  return Object.values(ALLOWED_ROLES_ENUM).includes(formattedValue);
};

const isRequiredTimeLapsed = (
  lastUpdatedEpoch: number,
  hoursDifferenceInput: number = 4
): boolean => {
  const currentDate = getCurrentTime();
  const timeDifference = currentDate - lastUpdatedEpoch;
  const hoursDifference = timeDifference / (1000 * 60 * 60);
  return hoursDifference >= hoursDifferenceInput;
};

const isValidEmail = (mail: string): boolean => REGEX.email.test(mail);

const getCurrentTime = () => Date.now();

const getDateDifference = (
  date1: string,
  date2: string,
  unit: unitOfTime.Diff = "days"
) => {
  return moment(date1).diff(moment(date2), unit);
};

const getDateValueInMoment = (date: string) => {
  if (moment.isMoment(date)) {
    return date;
  }
  if (date) {
    return moment(date);
  }
  return moment();
};

const getDatePickerMaxDate = (date: string, yearsToAdd: number = 5) =>
  getDateValueInMoment(date).add(date ? 0 : yearsToAdd, "years");

const getDatePickerMinDate = (date: string) => getDateValueInMoment(date);

const getFormattedTableDetails = (rawData: ObjectType[]) => {
  const formattedTableData: ObjectType[] = [];
  rawData.forEach((rowDetails: ObjectType) => {
    const formattedDetails: ObjectType = {};
    Object.keys(rowDetails).forEach((key: string) => {
      if (typeof rowDetails[key] === "object" && rowDetails[key]?.label) {
        formattedDetails[`${key}`] = rowDetails[key]?.label;
        formattedDetails[`${key}Id`] = rowDetails[key]?.value;
      } else {
        formattedDetails[`${key}`] = rowDetails[key] ?? "";
      }
    });
    formattedTableData.push(formattedDetails);
  });
  return formattedTableData;
};

const getIso8601Format = (date?: string) =>
  date ? moment(date).format("YYYY-MM-DDTHH:mm:ss[Z]") : "";

const formatDateFromServerUTC = (
  dateString: string,
  locale: string = "en-IN"
) => {
  if (!dateString) {
    return "";
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return dateString;
  }
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
};

const replaceVariables = (str: string, values: Record<string, string>) => {
  return str.replace(/\{(\w+)\}/g, (match, key) => values[key] || match);
};

const toProperCase = (str: string) => {
  if (!str) {
    return "";
  }
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const getFullName = (
  firstName: string = "",
  middleName: string = "",
  lastName: string = ""
) =>
  toProperCase(
    `${firstName}${middleName ? " " + middleName : ""}${lastName ? " " + lastName : ""}`
  );

const getDropdownDataFromArray = (arr: string[] = []): DropdownType[] =>
  arr?.length
    ? arr?.map((element: string) => ({
        label: toProperCase(element?.replaceAll?.("_", " ")),
        value: element,
      }))
    : [];

const getMemberStatusDropdownOptions = (): DropdownType[] => [
  {
    value: "",
    label: "all",
  },
  ...getDropdownDataFromArray(Object.keys(MEMBER_STATUS_ENUM)),
];

const extractNumbers = (input: string) => {
  if (typeof input !== "string") return "";
  return input.replace(/\D/g, "");
};

const handleExport = (data: ExportMember[]): void => {
  const excludedKeys = [
    "_id",
    "surnameId",
    "genderId",
    "bloodGroupId",
    "hideSensitiveData",
    "mosalSurnameId",
    "maritalStatusId",
    "familyHeadMemberId",
    "familyHeadRelationId",
    "deleted",
    "deleteRequestedAt",
    "profileImageUrl",
  ];

  // Clean + transform data
  const cleanedData = data.map((item) => {
    const filtered: Record<string, string | number> = {};
    Object.keys(item).forEach((key) => {
      if (!excludedKeys.includes(key)) {
        let value = (item as any)[key];
        if (value === null || value === undefined || value === "") value = "";

        // Format date fields
        if (key.toLowerCase().includes("date") && value) {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            const day = date.getDate().toString().padStart(2, "0");
            const month = date.toLocaleString("en-US", { month: "short" });
            const year = date.getFullYear();
            value = `${day}-${month}-${year}`;
          }
        }

        filtered[formatAndCapitalize(key)] = value;
      }
    });
    return filtered;
  });

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(cleanedData);

  // Auto column width
  const colWidths = Object.keys(cleanedData[0]).map((key) => ({
    wch:
      Math.max(
        key.length,
        ...cleanedData.map((row) => (row[key] ? row[key].toString().length : 0))
      ) + 2,
  }));
  worksheet["!cols"] = colWidths;

  // Header styling
  const range = XLSX.utils.decode_range(worksheet["!ref"]!);
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
    if (worksheet[cellAddress]) {
      worksheet[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4F81BD" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };
    }
  }

  // Alternate row colors + borders
  for (let R = 1; R <= range.e.r; ++R) {
    const isEven = R % 2 === 0;
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!worksheet[cellAddress]) continue;
      worksheet[cellAddress].s = {
        fill: { fgColor: { rgb: isEven ? "F2F2F2" : "FFFFFF" } },
        border: {
          top: { style: "thin", color: { rgb: "CCCCCC" } },
          bottom: { style: "thin", color: { rgb: "CCCCCC" } },
          left: { style: "thin", color: { rgb: "CCCCCC" } },
          right: { style: "thin", color: { rgb: "CCCCCC" } },
        },
      };
    }
  }

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "User Data");

  // Save as XLSX
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    cellStyles: true,
  });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });
  saveAs(blob, `MembersData_Export_${getCurrentDateLongFormat()}.xlsx`);
};

const getCurrentDateLongFormat = (): string => {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0"); // 01–12
  const day = String(now.getDate()).padStart(2, "0"); // 01–31
  const hours = String(now.getHours()).padStart(2, "0"); // 00–23
  const minutes = String(now.getMinutes()).padStart(2, "0"); // 00–59
  const seconds = String(now.getSeconds()).padStart(2, "0"); // 00–59

  return `${year}${month}${day}_${hours}${minutes}${seconds}`;
};

const formatAndCapitalize = (text: string): string => {
  if (!text) return "";
  return (
    text
      // Replace underscores or hyphens with spaces
      .replace(/[_-]+/g, " ")
      // Add space before capital letters in camelCase
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      // Trim extra spaces
      .trim()
      // Capitalize each word
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
};

export {
  configureSidebarOptions,
  errorHandler,
  formatDateFromServerUTC,
  getCurrentTime,
  getDateDifference,
  getDatePickerMaxDate,
  getDatePickerMinDate,
  getDateValueInMoment,
  getDropdownDataFromArray,
  getFormattedTableDetails,
  getFullName,
  getIso8601Format,
  getMemberStatusDropdownOptions,
  isRequiredTimeLapsed,
  isRoleAllowed,
  isValidEmail,
  replaceVariables,
  toProperCase,
  extractNumbers,
  handleExport,
};
